import dayjs from 'dayjs';
import { PropTypes } from 'prop-types';
import React, { useCallback, useRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Badge } from '@ge/components/badge';
import { Button } from '@ge/components/button';
import { Loader, SpinLoader } from '@ge/components/loader';
import { Dialog } from '@ge/components/modal';
import { DraggableTable, DynamicTable } from '@ge/components/table';
import { TableArrow } from '@ge/components/table';
import { useTableFactories } from '@ge/components/table/use-table-factories';
import AddDamageDialog from '@ge/feat-monitor/components/add-damage-dialog';
import { MonitorDefs, Placeholders, EntityType } from '@ge/models/constants';
import { DamgesComponent } from '@ge/models/constants';
import { killEventPropagation } from '@ge/shared/util/general';
import { globalColors } from '@ge/tokens';
import { useDetachCaseDamages } from '@ge/web-client/src/app/components/entity-details/case-details/data-hooks/use-detach-case-damages';

import GearBoxOrange from '../../../../apps/web-client/src/app/components/entity-details/GearBox_Orange.svg';
import GearBoxRed from '../../../../apps/web-client/src/app/components/entity-details/GearBox_Red.svg';
import GearBoxYellow from '../../../../apps/web-client/src/app/components/entity-details/GearBox_Yellow.svg';
import GeneratorOrange from '../../../../apps/web-client/src/app/components/entity-details/Generator_Orange.svg';
import GeneratorRed from '../../../../apps/web-client/src/app/components/entity-details/Generator_Red.svg';
import GeneratorYellow from '../../../../apps/web-client/src/app/components/entity-details/Generator_Yellow.svg';
import MainBearingOrange from '../../../../apps/web-client/src/app/components/entity-details/MainBearing_Orange.svg';
import MainBearingRed from '../../../../apps/web-client/src/app/components/entity-details/MainBearing_Red.svg';
import MainBearingYellow from '../../../../apps/web-client/src/app/components/entity-details/MainBearing_Yellow.svg';
import PitchBearingOrange from '../../../../apps/web-client/src/app/components/entity-details/Pitch_Bearing_Orange.svg';
import PitchBearingRed from '../../../../apps/web-client/src/app/components/entity-details/Pitch_Bearing_Red.svg';
import PitchBearingYellow from '../../../../apps/web-client/src/app/components/entity-details/Pitch_Bearing_Yellow.svg';
import { useActionsMenu } from '../../data-hooks';
import { useTableSelection } from '../../data-hooks/use-table-selection';
import { useFilterDefs } from '../../hooks';
import { ActionKeys, CaseDamageTableMenuItems } from '../../models/actions-menu-items';
import {
  CaseDamagesColumnDefs,
  CaseDamagesColumns,
  defaultCaseDamagesCols,
} from '../../models/table-col-defs/case-damage-table-cols';
import ActionsMenu from '../actions-menu';

const StyledBadge = styled(Badge)`
  border-radius: 10px;
`;

const CellText = styled.span`
  font-size: 11px;
  font-weight: 300;
`;

const CellImageIcon = styled.span`
  margin-left: 10px;
  .image_icon {
    margin-bottom: -3px;
  }
  .bearing_image_icon {
    margin-bottom: -10px;
  }
  .pitch_bearing_image_icon {
    margin-bottom: -10px;
    height: 20px;
    width: 20px;
  }
`;

const StyledDialog = styled(Dialog)`
  margin-top: 10%;
  header {
    border-radius: 4px 4px 0 0;
  }
  footer {
    border-radius: 0 0 4px 4px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: end;
`;

const CancelButton = styled(Button)`
  width: 75px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.caseDamages.cancelBtnBorderColor};
  background: ${(props) => props.theme.caseDamages.detachBtnBgColor};
  font-size: 13px;
  font-weight: 600;
  line-height: 15.6px;
  text-align: center;
  color: ${(props) => props.theme.caseDamages.textColor};
  margin-right: 10px;
`;

const DeleteButton = styled(Button)`
  width: 72px;
  height: 24px
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.caseDamages.confirmBtnBorderColor};
  background: ${(props) => props.theme.caseDamages.confirmBtnBgColor};
  font-size: 13px;
  font-weight: 600;
  line-height: 15.6px;
  text-align: center;
  color: ${(props) => props.theme.caseDamages.textColor};
`;

const CaseDetailButton = styled.button`
  padding: 8px 0;
  width: 100%;
`;

const CaseDamagesTable = ({
  caseDamages,
  columns,
  sortAction,
  sortMetric,
  sortDirection,
  draggable,
  scrollable,
  onFilter,
  onFilterChange,
  filterValues,
  setCheckedCaseDamages,
  isCloseTriggered,
  setIsCloseTriggered,
  showDetachModal,
  setShowDetachModal,
  caseId,
  caseDetail,
  isAssetPanel,
  handleSelectDamage,
}) => {
  const { t, ready } = useTranslation(['monitor.issues'], {
    useSuspense: false,
  });
  const containerRef = useRef(null);
  const [showUpdateDamageDialog, setShowUpdateDamageDialog] = useState(false);
  const [selectedCaseDamage, setSelectedCaseDamage] = useState(null);
  const [caseDamagesColumnDefs, setCaseDamagesColumnDefs] = useState(CaseDamagesColumnDefs);
  const getActionsMenuItems = useActionsMenu({ menuItems: CaseDamageTableMenuItems });

  const sortedDirection = useCallback(
    (metric) => (metric === sortMetric ? sortDirection : ''),
    [sortDirection, sortMetric],
  );

  const { filterDefs: caseFilterDefs, onChange: onCaseFilterApply } = useFilterDefs({
    columnDefs: caseDamagesColumnDefs,
    stateId: MonitorDefs.CASE_DAMAGES_FILTER_STATE_ID,
  });

  const { filterDefs: assetFilterDefs, onChange: onAssetFilterApply } = useFilterDefs({
    columnDefs: caseDamagesColumnDefs,
    stateId: MonitorDefs.ASSET_DAMAGES_FILTER_STATE_ID,
  });

  const { checkedRows, customHeaderFn, renderCheckbox, resetCheckedRows } = useTableSelection({
    data: caseDamages,
  });

  const { mutateAsync: detachCaseDamages, isLoading: isDetachCaseDamagesLoading } =
    useDetachCaseDamages(caseId, setShowDetachModal);

  useEffect(() => {
    if (isCloseTriggered) {
      resetCheckedRows();
      setIsCloseTriggered(false);
    }
  }, [isCloseTriggered, resetCheckedRows, setIsCloseTriggered]);

  useEffect(() => {
    if (isAssetPanel) {
      const keysToFilter = ['group-selected', 'group-action'];
      const filteredAssetColumnData = Object.fromEntries(
        Object.entries(CaseDamagesColumnDefs).filter(([key]) => !keysToFilter.includes(key)),
      );
      setCaseDamagesColumnDefs(filteredAssetColumnData);
    } else {
      const keysToFilter = ['group-details'];
      const filteredCaseColumnData = Object.fromEntries(
        Object.entries(CaseDamagesColumnDefs).filter(([key]) => !keysToFilter.includes(key)),
      );
      setCaseDamagesColumnDefs(filteredCaseColumnData);
    }
  }, [isAssetPanel]);

  useEffect(() => {
    if (!isAssetPanel) {
      setCheckedCaseDamages(checkedRows?.length);
    }
  }, [checkedRows, setCheckedCaseDamages, isAssetPanel]);

  useEffect(() => {
    if (isAssetPanel) {
      onFilter(assetFilterDefs);
    } else {
      onFilter(caseFilterDefs);
    }
  }, [onFilter, isAssetPanel, assetFilterDefs, caseFilterDefs]);

  const handleFilterApply = useCallback(
    (_, columnKey, value) => {
      if (isAssetPanel) {
        onAssetFilterApply(columnKey, value);
      } else {
        onCaseFilterApply(columnKey, value);
      }
    },
    [isAssetPanel, onAssetFilterApply, onCaseFilterApply],
  );

  const handleFilterChange = useCallback(
    (_, key, value) => onFilterChange({ key, value }),
    [onFilterChange],
  );

  const handleActionMenu = useCallback(
    ({ entity, type }) => {
      if (type === ActionKeys.DETACH_CASE_DAMAGE) {
        setShowDetachModal(true);
      }
      if (type === ActionKeys.UPDATE_CASE_DAMAGE) {
        setShowUpdateDamageDialog(true);
      }
      setSelectedCaseDamage(caseDamages?.find((damage) => damage.id === entity));
    },
    [caseDamages, setShowDetachModal],
  );

  const handleClose = useCallback(
    (event) => {
      killEventPropagation(event);
      setSelectedCaseDamage(null);
      setShowDetachModal(false);
    },
    [setShowDetachModal],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const imageMap = {
    Generator3: GeneratorYellow,
    Generator4: GeneratorOrange,
    Generator5: GeneratorRed,
    Main_Bearing3: MainBearingYellow,
    Main_Bearing4: MainBearingOrange,
    Main_Bearing5: MainBearingRed,
    Gearbox3: GearBoxYellow,
    Gearbox4: GearBoxOrange,
    Gearbox5: GearBoxRed,
    Pitch_Bearing3: PitchBearingYellow,
    Pitch_Bearing4: PitchBearingOrange,
    Pitch_Bearing5: PitchBearingRed,
  };

  const getImageName = useCallback(
    (damage) => {
      const damgageComponent = damage?.component?.trim().replace(/ /g, '_');
      const damageCategory = damage?.category?.trim().replace(/\D+/g, '');
      return imageMap[`${damgageComponent}${damageCategory}`];
    },
    [imageMap],
  );

  const handleDetach = useCallback(
    (event) => {
      killEventPropagation(event);
      const body = {
        action: 'removeDamage',
        id: caseId,
        damages: selectedCaseDamage
          ? [
              {
                damageId: selectedCaseDamage.id,
                category: selectedCaseDamage.category,
                pacNumber: selectedCaseDamage.pacNumber,
                pacUrl: selectedCaseDamage.pacUrl,
                status: selectedCaseDamage.status,
              },
            ]
          : checkedRows?.map((id) => {
              let data = caseDamages.find((damage) => damage.id == id);
              return {
                damageId: id,
                category: data.category,
                pacNumber: data.pacNumber,
                pacUrl: data.pacUrl,
                status: data.status,
              };
            }),
      };
      detachCaseDamages(body);
      setSelectedCaseDamage(null);
    },
    [caseDamages, caseId, checkedRows, detachCaseDamages, selectedCaseDamage],
  );

  const getFooter = useMemo(
    () => (
      <>
        <SpinLoader showLoader={isDetachCaseDamagesLoading} />
        <ButtonContainer onClick={(e) => e.stopPropagation()}>
          <CancelButton onClick={handleClose}>
            {t('case_damages_dialog.cancel', 'CANCEL')}
          </CancelButton>
          <DeleteButton primary onClick={() => handleDetach()}>
            {t('case_damages_dialog.delete', 'DELETE')}
          </DeleteButton>
        </ButtonContainer>
      </>
    ),
    [handleClose, handleDetach, isDetachCaseDamagesLoading, t],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps

  const cellValueMapFn = useCallback((caseDamages) => {
    if (!caseDamages) {
      return {};
    }
    const {
      id,
      status,
      category,
      component,
      location,
      part,
      damage,
      followupAction,
      creationDate,
      rulStartDate,
      followupDate,
    } = caseDamages;
    return {
      [CaseDamagesColumns.SELECTED]: id,
      [CaseDamagesColumns.STATUS]: status,
      [CaseDamagesColumns.CATEGORY]: category,
      [CaseDamagesColumns.COMPONENT]: component,
      [CaseDamagesColumns.LOCATION]: location,
      [CaseDamagesColumns.PART]: part,
      [CaseDamagesColumns.DAMAGE]: damage,
      [CaseDamagesColumns.FOLLOW_UP_ACTION]: followupAction,
      [CaseDamagesColumns.CREATION_DATE]: creationDate,
      [CaseDamagesColumns.RUL_START_DATE]: rulStartDate,
      [CaseDamagesColumns.FOLLOWUP_DATE]: followupDate,
      [CaseDamagesColumns.ACTION]: id,
      [CaseDamagesColumns.DETAILS]: id,
    };
  }, []);

  const customCellFn = useCallback(
    (columnKey, cellValue, damage) => {
      const imageName = getImageName(damage);
      const damageImageName = imageName ? imageName : '';
      if (!cellValue) return null;
      switch (columnKey) {
        case CaseDamagesColumns.SELECTED:
          return renderCheckbox(cellValue);
        case CaseDamagesColumns.STATUS:
          return <StyledBadge color={globalColors.slate3} label={`${cellValue}`} />;
        case CaseDamagesColumns.CATEGORY:
          return <CellText>{cellValue}</CellText>;
        case CaseDamagesColumns.LOCATION:
          return <CellText>{cellValue}</CellText>;
        case CaseDamagesColumns.PART:
          return <CellText>{cellValue}</CellText>;
        case CaseDamagesColumns.DAMAGE:
          return <CellText>{cellValue}</CellText>;
        case CaseDamagesColumns.FOLLOW_UP_ACTION:
          return <CellText>{cellValue}</CellText>;
        case CaseDamagesColumns.COMPONENT:
          return (
            <>
              <CellText>{cellValue}</CellText>
              {damageImageName && isAssetPanel && damage?.status === 'Open' && (
                <CellImageIcon>
                  <img
                    src={damageImageName}
                    alt="Icon"
                    className={
                      damage?.component === DamgesComponent.main_bearing
                        ? 'bearing_image_icon'
                        : damage?.component === DamgesComponent.pitch_bearing
                        ? 'pitch_bearing_image_icon'
                        : 'image_icon'
                    }
                  />
                </CellImageIcon>
              )}
            </>
          );
        case CaseDamagesColumns.CREATION_DATE:
          return (
            <CellText>
              {cellValue ? dayjs(cellValue * 1000).format('DD MMM YY') : Placeholders.DASH}
            </CellText>
          );
        case CaseDamagesColumns.RUL_START_DATE:
          return (
            <CellText>
              {cellValue ? dayjs(cellValue * 1000).format('DD MMM YY') : Placeholders.DASH}
            </CellText>
          );
        case CaseDamagesColumns.FOLLOWUP_DATE:
          return (
            <CellText>
              {cellValue ? dayjs(cellValue * 1000).format('DD MMM YY') : Placeholders.DASH}
            </CellText>
          );
        case CaseDamagesColumns.ACTION:
          return (
            <ActionsMenu
              containerRef={containerRef}
              entity={cellValue}
              menuIconSmall
              menuItems={getActionsMenuItems([cellValue])}
              onAction={handleActionMenu}
            />
          );
        case CaseDamagesColumns.DETAILS:
          return (
            <CaseDetailButton
              type="button"
              onClick={(e) => {
                killEventPropagation(e);
                e.preventDefault();
                handleSelectDamage(e, cellValue);
              }}
            >
              <TableArrow />
            </CaseDetailButton>
          );
        default:
          return null;
      }
    },
    [
      getActionsMenuItems,
      getImageName,
      handleActionMenu,
      handleSelectDamage,
      isAssetPanel,
      renderCheckbox,
    ],
  );

  /**
   * Bootstrap table factories
   */
  const [columnGroupFactory, columnFactory, cellFactory] = useTableFactories({
    t,
    columnDefs: caseDamagesColumnDefs,
    cellValueMapFn,
    customHeaderFn,
    customCellFn,
    sortAction,
    sortedDirection,
    filters: isAssetPanel ? assetFilterDefs : caseFilterDefs,
    filterValues,
    onFilterApply: handleFilterApply,
    onFilterChange: handleFilterChange,
  });

  const dynamicProps = {
    scrollable,
    compressed: true,
    transparent: true,
    noTitles: true,
    columns,
    columnGroupFactory,
    columnFactory,
    cellFactory,
    sortAction,
    values: caseDamages,
    onValueSelect: () => null,
    rowKeyProperty: 'id',
  };

  const CaseDamageTable = useMemo(() => {
    if (draggable) return DraggableTable;
    return DynamicTable;
  }, [draggable]);

  if (!ready) {
    return <Loader />;
  }

  return (
    <>
      <CaseDamageTable {...dynamicProps} />
      {showDetachModal && (
        <StyledDialog
          isOpen={true}
          header={t('case_damages_dialog.header_text', 'Detach Damage')}
          onClose={handleClose}
          footer={getFooter}
        >
          <span>
            {t(
              'case_damages_dialog.confirmation_message',
              'Do you want to detach the selected damage(s)?',
            )}
          </span>
        </StyledDialog>
      )}
      {showUpdateDamageDialog ? (
        <AddDamageDialog
          caseDetail={caseDetail}
          entityType={EntityType.CASE}
          onClose={(e) => {
            killEventPropagation(e);
            setShowUpdateDamageDialog(false);
          }}
          isUpdate={true}
          exisitingDamage={selectedCaseDamage}
        />
      ) : null}
    </>
  );
};

CaseDamagesTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  caseDamages: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  sortAction: PropTypes.func,
  sortMetric: PropTypes.string,
  sortDirection: PropTypes.string,
  onDamageSelect: PropTypes.func,
  draggable: PropTypes.bool,
  scrollable: PropTypes.bool,
  onFilter: PropTypes.func,
  onFilterChange: PropTypes.func,
  filterValues: PropTypes.object,
  setCheckedCaseDamages: PropTypes.func,
  isCloseTriggered: PropTypes.bool,
  setIsCloseTriggered: PropTypes.func,
  showDetachModal: PropTypes.bool,
  setShowDetachModal: PropTypes.func,
  caseId: PropTypes.string,
  caseDetail: PropTypes.instanceOf(Object).isRequired,
  isAssetPanel: PropTypes.bool,
  handleSelectDamage: PropTypes.func,
};

CaseDamagesTable.defaultProps = {
  columns: defaultCaseDamagesCols,
  sortAction: () => null,
  sortMetric: '',
  sortDirection: '',
  onDamageSelect: () => null,
  draggable: false,
  scrollable: false,
  onFilter: () => null,
  onFilterChange: () => null,
  filterValues: {},
  isCloseTriggered: false,
  caseId: '',
  isAssetPanel: false,
  handleSelectDamage: () => null,
};

export default CaseDamagesTable;
